import type { GameState, Language } from "../core/types";
import { parseSave, validateSave, MAX_SAVE_BYTES } from "./schema";
export interface Preferences {
  language: Language;
  master: number;
  music: number;
  sfx: number;
  muted: boolean;
  reducedMotion: boolean;
  contrast: boolean;
  speed: number;
}
export const defaults: Preferences = {
  language: "en",
  master: 0.8,
  music: 0.65,
  sfx: 0.75,
  muted: false,
  reducedMotion: false,
  contrast: false,
  speed: 1,
};
const PREF = "stellar-empire.preferences.v1",
  PREFIX = "stellar-empire.save.";
export function loadPreferences(): Preferences {
  try {
    const p = JSON.parse(localStorage.getItem(PREF) ?? "{}");
    return {
      language: p.language === "de" ? "de" : "en",
      master: volume(p.master, defaults.master),
      music: volume(p.music, defaults.music),
      sfx: volume(p.sfx, defaults.sfx),
      muted: p.muted === true,
      reducedMotion: p.reducedMotion === true,
      contrast: p.contrast === true,
      speed: [0.5, 1, 2].includes(p.speed) ? p.speed : 1,
    };
  } catch {
    return { ...defaults };
  }
}
function volume(n: unknown, f: number) {
  return typeof n === "number" && Number.isFinite(n)
    ? Math.max(0, Math.min(1, n))
    : f;
}
export function savePreferences(p: Preferences) {
  try {
    localStorage.setItem(PREF, JSON.stringify(p));
    return true;
  } catch {
    return false;
  }
}
export type Slot = `manual-${1 | 2 | 3 | 4 | 5}` | `auto-${1 | 2 | 3}`;
export interface SaveRecord {
  slot: Slot;
  date: string;
  name: string;
  game: GameState;
}
export const SLOTS: Slot[] = [
  "manual-1",
  "manual-2",
  "manual-3",
  "manual-4",
  "manual-5",
  "auto-1",
  "auto-2",
  "auto-3",
];
export class SaveStore {
  private db: IDBDatabase | null = null;
  private memory = new Map<Slot, SaveRecord>();
  private initialized = false;
  private autoQueue: Promise<void> = Promise.resolve();
  mode: "indexeddb" | "localstorage" | "memory" = "memory";
  async init() {
    if (this.initialized) return;
    this.initialized = true;
    try {
      this.db = await new Promise<IDBDatabase>((resolve, reject) => {
        const r = indexedDB.open("stellar-empire", 1);
        r.onupgradeneeded = () =>
          r.result.createObjectStore("saves", { keyPath: "slot" });
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
        r.onblocked = () => reject(new Error("Storage blocked"));
      });
      this.mode = "indexeddb";
      return;
    } catch {}
    try {
      const key = PREFIX + "probe";
      localStorage.setItem(key, "1");
      localStorage.removeItem(key);
      this.mode = "localstorage";
    } catch {
      this.mode = "memory";
    }
  }
  private async query<T>(
    mode: IDBTransactionMode,
    op: (s: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction("saves", mode);
      let value: T;
      const r = op(tx.objectStore("saves"));
      r.onsuccess = () => (value = r.result);
      r.onerror = () => reject(r.error);
      tx.oncomplete = () => resolve(value);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error("Save aborted"));
    });
  }
  async list() {
    await this.init();
    let records: SaveRecord[] = [];
    if (this.mode === "indexeddb")
      records = await this.query("readonly", (s) => s.getAll());
    else if (this.mode === "localstorage") {
      for (const slot of SLOTS) {
        try {
          const v = localStorage.getItem(PREFIX + slot);
          if (v) records.push(JSON.parse(v));
        } catch {}
      }
    } else records = [...this.memory.values()];
    return records
      .filter((x) => {
        try {
          return (
            SLOTS.includes(x.slot) &&
            typeof x.date === "string" &&
            Number.isFinite(Date.parse(x.date)) &&
            typeof x.name === "string" &&
            !!validateSave(x.game)
          );
        } catch {
          return false;
        }
      })
      .sort((a, b) => a.slot.localeCompare(b.slot));
  }
  async read(slot: Slot) {
    const record = (await this.list()).find((x) => x.slot === slot);
    if (!record) throw new Error("save.missing");
    return validateSave(record.game);
  }
  async write(slot: Slot, game: GameState, name = "") {
    if (!SLOTS.includes(slot)) throw new Error("save.invalid");
    const safe = validateSave(game),
      json = JSON.stringify(safe);
    if (new TextEncoder().encode(json).byteLength > MAX_SAVE_BYTES)
      throw new Error("save.size");
    await this.init();
    const record: SaveRecord = {
      slot,
      date: new Date().toISOString(),
      name: name.slice(0, 60).replace(/[<>]/g, ""),
      game: safe,
    };
    if (this.mode === "indexeddb")
      await this.query("readwrite", (s) => s.put(record));
    else if (this.mode === "localstorage")
      localStorage.setItem(PREFIX + slot, JSON.stringify(record));
    else this.memory.set(slot, structuredClone(record));
    return record;
  }
  async remove(slot: Slot) {
    await this.init();
    if (this.mode === "indexeddb")
      await this.query("readwrite", (s) => s.delete(slot));
    else if (this.mode === "localstorage")
      localStorage.removeItem(PREFIX + slot);
    else this.memory.delete(slot);
  }
  async auto(game: GameState) {
    const snapshot = validateSave(game),
      task = this.autoQueue.then(async () => {
        const records = (await this.list()).filter((x) =>
            x.slot.startsWith("auto-"),
          ),
          slot =
            SLOTS.filter((s) => s.startsWith("auto-")).find(
              (s) => !records.some((r) => r.slot === s),
            ) ?? records.sort((a, b) => a.date.localeCompare(b.date))[0].slot;
        await this.write(slot, snapshot, "");
      });
    this.autoQueue = task.catch(() => {});
    return task;
  }
  async latest() {
    return (
      (await this.list()).sort((a, b) => b.date.localeCompare(a.date))[0] ??
      null
    );
  }
}
export async function importFile(file: File) {
  if (file.size > MAX_SAVE_BYTES) throw new Error("save.size");
  return parseSave(await file.text());
}
export function exportSave(game: GameState) {
  const text = JSON.stringify(validateSave(game), null, 2);
  const url = URL.createObjectURL(
    new Blob([text], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `stellar-empire-save-${new Date().toISOString().slice(0, 10).replaceAll("-", "")}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
